import React from "react"
import {
    Flex,
    Text,
    Image,
    Rte,
    INNER_MAX_WIDTH,
    READABLE_LINE_MAX_WIDTH,
} from "@capioweb/ui-components/src"
import {
    ContentAreaProperty,
    XhtmlString,
    QBankImage,
} from "../../@types/CapioWeb/core"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import {
    PageBaseProps,
    BreadcrumbItemProps,
} from "../../components/Core/pageBase"
import { BlockResolver, BreadcrumbsBlock } from "../../components"
import { AnchorLinkContext, EpiContext } from "../../context"
import {
    shouldRender,
    imageBlockDataToImageProps,
    createMarkup,
} from "../../utils"
import { useLocation } from "react-router-dom"
import { scrollToAnchor } from "../../utils/scrollToAnchor"

interface CapioStandardNewsPageProps extends PageBaseProps {
    heading: StringProperty
    preamble?: StringProperty
    body?: XhtmlString
    imageDesktopBlock?: QBankImage
    mainContentArea: ContentAreaProperty
    cta?: ContentAreaProperty
    breadcrumb: Array<BreadcrumbItemProps>
    labelText?: StringProperty
    publishingDate?: string
}

const CapioStandardNewsPage: React.FC<CapioStandardNewsPageProps> = (
    props
) => {
    const { editMode } = React.useContext(EpiContext)
    const hasImage = props.imageDesktopBlock?.media?.value?.url ? true : false
    const srcSetSizes = [
        { width: 1200, breakpoint: 768 },
        { width: 1600, breakpoint: 2560 },
    ]

    const { hash } = useLocation()

    const id = decodeURI(hash.substring(1))

    const el = document?.getElementById(id)

    React.useEffect(() => {
        if (el) {
            //Scroll to anchorID position if it exists
            scrollToAnchor(el)
        }
    }, [el]) //UseEffect will run again when 'el' is ready

    return (
        <Flex display="block" w={1}>
            <BreadcrumbsBlock items={props.breadcrumb} />
            <Flex as="section" w={1} px={3}>
                <Flex
                    w={1}
                    flexDirection="column"
                    maxw={INNER_MAX_WIDTH}
                    style={{ margin: "0 auto" }}
                >
                    <Flex
                        w={{ _: "calc(100% + 48px)", m: 1 }}
                        maxw={READABLE_LINE_MAX_WIDTH}
                        ml={{ _: -3, m: 0 }}
                        mb={hasImage ? 4 : 5}
                    >
                        {hasImage && (
                            <Image
                                data-epi-edit="imageDesktopBlock"
                                {...imageBlockDataToImageProps(
                                    props.imageDesktopBlock,
                                    true,
                                    true,
                                    srcSetSizes
                                )}
                                ratio={{
                                    _: 420 / 721,
                                    l: 9 / 16,
                                }}
                                cover
                                caption={props?.imageDesktopBlock?.imageCaption?.value}
                            />
                        )}
                    </Flex>
                    <Flex
                        w={1}
                        flexDirection={{ _: "column", l: "row" }}
                        columnGap={{ l: 3 }}
                    >
                        <Flex
                            w={1}
                            flex={{ _: 1, l: 8 / 12 }}
                            flexDirection="column"
                            mb={{ _: 2, m: 4 }}
                            maxw={READABLE_LINE_MAX_WIDTH}
                        >
                            {props.heading?.value && (
                                <Text variant="headline-1">{props.heading.value}</Text>
                            )}

                            <Flex mt={2}>
                                {props.labelText?.value && (
                                    <Text
                                        variant="headline-4"
                                        textTransform="uppercase"
                                        mb={{ _: 0, xs: 3 }}
                                        lineHeight={3}
                                    >
                                        {props.labelText?.value}
                                    </Text>
                                )}
                                {props.labelText?.value && props.publishingDate && (
                                    <Flex
                                        mx={{ _: 1, xs: 2 }}
                                        w="1px"
                                        h={3}
                                        backgroundColor="blue33"
                                    ></Flex>
                                )}
                                {props.publishingDate && (
                                    <Text variant="paragraph-16">{props.publishingDate}</Text>
                                )}
                            </Flex>

                            {props.preamble?.value && (
                                <Text variant="preamble">{props.preamble.value}</Text>
                            )}

                            {props.cta?.value && (
                                <Flex
                                    w="auto"
                                    mb={3}
                                    display={{ _: "flex", l: "none" }}
                                    flexDirection="column"
                                    rowGap={2}
                                >
                                    <BlockResolver blocks={props.cta?.value} />
                                </Flex>
                            )}

                            {props.body?.value && (
                                <Rte>
                                    <div
                                        dangerouslySetInnerHTML={createMarkup(props.body.value)}
                                    />
                                </Rte>
                            )}
                        </Flex>
                        {props.cta?.value && (
                            <Flex
                                w={1}
                                mb={3}
                                flex={{ _: 1, l: 4 / 12 }}
                                alignSelf="flex-start"
                                display={{ _: "none", l: "flex" }}
                                flexDirection="column"
                                rowGap={3}
                            >
                                <BlockResolver blocks={props.cta?.value} />
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            </Flex>
            {shouldRender(editMode.inEditMode, props.mainContentArea?.value) && (
                <div data-epi-edit={"mainContentArea"}>
                    <AnchorLinkContext.Provider
                        value={{
                            activeId: id ?? null,
                        }}
                    >
                        <BlockResolver blocks={props.mainContentArea?.value} />
                    </AnchorLinkContext.Provider>
                </div>
            )}
        </Flex>
    )
}

export { CapioStandardNewsPage }
